/**
 * CheckInInsightsTab Component
 * Analytics and insights for managers about their team's check-ins
 */

import React, { useState } from 'react';
import {
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  Clock,
  Star,
  Users,
  TrendingUp,
  Building2
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Card, Badge, LoadingSpinner, EmptyState, Avatar } from '../../components/common';
import { useCheckInInsights, useOrgCheckInInsights } from '../../hooks';
import type { TeamMemberOverview, CheckInStatus } from '../../types/okr';

interface CheckInInsightsTabProps {
  isAccountAdmin?: boolean;
}

const statusConfig: Record<CheckInStatus, { color: string; bgColor: string; label: string }> = {
  on_track: { color: 'text-success-600', bgColor: 'bg-success-500', label: 'On Track' },
  at_risk: { color: 'text-warning-600', bgColor: 'bg-warning-500', label: 'At Risk' },
  off_track: { color: 'text-danger-600', bgColor: 'bg-danger-500', label: 'Off Track' },
};

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: typeof CheckCircle;
  iconColor: string;
  iconBg: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, subtitle, icon: Icon, iconColor, iconBg }) => (
  <Card className="p-5">
    <div className="flex items-center gap-4">
      <div className={`p-3 rounded-xl ${iconBg}`}>
        <Icon className={`h-6 w-6 ${iconColor}`} />
      </div>
      <div>
        <p className="text-2xl font-semibold text-stone-900">{value}</p>
        <p className="text-sm text-stone-500">{title}</p>
        {subtitle && <p className="text-xs text-stone-400 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  </Card>
);

interface HealthBarProps {
  onTrack: number;
  atRisk: number;
  offTrack: number;
  noCheckIn: number;
  total: number;
}

const HealthBar: React.FC<HealthBarProps> = ({ onTrack, atRisk, offTrack, noCheckIn, total }) => {
  if (total === 0) return null;

  const getPercent = (value: number) => Math.round((value / total) * 100);

  return (
    <div>
      {/* Bar */}
      <div className="h-4 rounded-full overflow-hidden flex bg-stone-200">
        {onTrack > 0 && (
          <div
            className="bg-success-500 transition-all"
            style={{ width: `${getPercent(onTrack)}%` }}
            title={`On Track: ${onTrack}`}
          />
        )}
        {atRisk > 0 && (
          <div
            className="bg-warning-500 transition-all"
            style={{ width: `${getPercent(atRisk)}%` }}
            title={`At Risk: ${atRisk}`}
          />
        )}
        {offTrack > 0 && (
          <div
            className="bg-danger-500 transition-all"
            style={{ width: `${getPercent(offTrack)}%` }}
            title={`Off Track: ${offTrack}`}
          />
        )}
        {noCheckIn > 0 && (
          <div
            className="bg-stone-400 transition-all"
            style={{ width: `${getPercent(noCheckIn)}%` }}
            title={`No Check-in: ${noCheckIn}`}
          />
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mt-3 text-xs">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-success-500" />
          <span className="text-stone-600">On Track ({onTrack})</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-warning-500" />
          <span className="text-stone-600">At Risk ({atRisk})</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-danger-500" />
          <span className="text-stone-600">Off Track ({offTrack})</span>
        </div>
        {noCheckIn > 0 && (
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-stone-400" />
            <span className="text-stone-600">No Check-in ({noCheckIn})</span>
          </div>
        )}
      </div>
    </div>
  );
};

interface TeamMemberCardProps {
  member: TeamMemberOverview;
}

const TeamMemberCard: React.FC<TeamMemberCardProps> = ({ member }) => {
  const statusInfo = member.lastStatus ? statusConfig[member.lastStatus] : null;

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Avatar
            size="md"
            src={member.avatar || undefined}
            fallbackText={member.userName}
          />
          <div>
            <p className="font-medium text-stone-900">{member.userName}</p>
            <div className="flex items-center gap-3 text-xs text-stone-500 mt-0.5">
              <span>{member.totalOKRs} OKRs</span>
              {member.pendingCheckIns > 0 && (
                <Badge variant="warning" size="sm">
                  {member.pendingCheckIns} pending
                </Badge>
              )}
            </div>
          </div>
        </div>

        <div className="text-right">
          {/* Last check-in */}
          {member.lastCheckIn ? (
            <div className="text-xs text-stone-500">
              Last check-in {formatDistanceToNow(new Date(member.lastCheckIn), { addSuffix: true })}
            </div>
          ) : (
            <div className="text-xs text-stone-400">No check-ins yet</div>
          )}

          {/* Status & Confidence */}
          <div className="flex items-center gap-2 mt-1 justify-end">
            {statusInfo && (
              <Badge
                variant={member.lastStatus === 'on_track' ? 'success' : member.lastStatus === 'at_risk' ? 'warning' : 'danger'}
                size="sm"
              >
                {statusInfo.label}
              </Badge>
            )}
            {member.averageConfidence && (
              <span className="flex items-center gap-1 text-xs text-stone-500">
                <Star className="h-3 w-3 text-warning-500 fill-warning-500" />
                {member.averageConfidence.toFixed(1)}
              </span>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export const CheckInInsightsTab: React.FC<CheckInInsightsTabProps> = ({ isAccountAdmin = false }) => {
  const [viewMode, setViewMode] = useState<'team' | 'org'>('team');

  // Fetch team insights
  const { data: teamData, isLoading: teamLoading, error: teamError, refetch: teamRefetch } = useCheckInInsights();

  // Fetch org insights (only for account admins)
  const { data: orgData, isLoading: orgLoading, error: orgError, refetch: orgRefetch } = useOrgCheckInInsights();

  // Use appropriate data based on view mode
  const data = viewMode === 'org' && isAccountAdmin ? orgData : teamData;
  const isLoading = viewMode === 'org' && isAccountAdmin ? orgLoading : teamLoading;
  const error = viewMode === 'org' && isAccountAdmin ? orgError : teamError;
  const refetch = viewMode === 'org' && isAccountAdmin ? orgRefetch : teamRefetch;

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" text="Loading insights..." />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <div className="text-center text-danger-600">
          <AlertCircle className="h-8 w-8 mx-auto mb-2" />
          <p>Failed to load insights. Please try again.</p>
          <button
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            Retry
          </button>
        </div>
      </Card>
    );
  }

  if (!data || data.teamOverview.length === 0) {
    return (
      <>
        {/* View Toggle for Account Admins */}
        {isAccountAdmin && (
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => setViewMode('team')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'team'
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
            >
              <Users className="h-4 w-4" />
              Team Insights
            </button>
            <button
              onClick={() => setViewMode('org')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'org'
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
            >
              <Building2 className="h-4 w-4" />
              Organization Insights
            </button>
          </div>
        )}
        <EmptyState
          icon={Users}
          title={viewMode === 'org' ? "No organization data" : "No team data"}
          description={viewMode === 'org'
            ? "No users with OKRs found in the organization. Insights will appear once employees have active OKRs."
            : "You don't have any direct reports with OKRs yet. Insights will appear here once your team members have active OKRs."
          }
        />
      </>
    );
  }

  const { healthDistribution, compliance, confidenceTrend, teamOverview } = data;

  // Calculate average confidence from trend
  const avgConfidence = confidenceTrend.length > 0
    ? (confidenceTrend.reduce((sum, t) => sum + t.averageConfidence, 0) / confidenceTrend.length).toFixed(1)
    : 'N/A';

  const overviewTitle = viewMode === 'org' && isAccountAdmin ? 'Organization Overview' : 'Team Overview';
  const okrSubtitle = viewMode === 'org' && isAccountAdmin ? 'Active organization OKRs' : 'Active team OKRs';

  return (
    <div className="space-y-6">
      {/* View Toggle for Account Admins */}
      {isAccountAdmin && (
        <div className="flex items-center gap-4">
          <button
            onClick={() => setViewMode('team')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'team'
                ? 'bg-primary-100 text-primary-700'
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
          >
            <Users className="h-4 w-4" />
            Team Insights
          </button>
          <button
            onClick={() => setViewMode('org')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'org'
                ? 'bg-primary-100 text-primary-700'
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
          >
            <Building2 className="h-4 w-4" />
            Organization Insights
          </button>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total OKRs"
          value={healthDistribution.total}
          subtitle={okrSubtitle}
          icon={TrendingUp}
          iconColor="text-primary-600"
          iconBg="bg-primary-100"
        />
        <StatCard
          title="On Track"
          value={healthDistribution.onTrack}
          subtitle={`${healthDistribution.total > 0 ? Math.round((healthDistribution.onTrack / healthDistribution.total) * 100) : 0}% of OKRs`}
          icon={CheckCircle}
          iconColor="text-success-600"
          iconBg="bg-success-100"
        />
        <StatCard
          title="At Risk"
          value={healthDistribution.atRisk + healthDistribution.offTrack}
          subtitle="Need attention"
          icon={AlertTriangle}
          iconColor="text-warning-600"
          iconBg="bg-warning-100"
        />
        <StatCard
          title="Avg Confidence"
          value={avgConfidence}
          subtitle="Last 30 days"
          icon={Star}
          iconColor="text-warning-600"
          iconBg="bg-warning-100"
        />
      </div>

      {/* Health Distribution */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-stone-900 mb-4">OKR Health Distribution</h3>
        <HealthBar
          onTrack={healthDistribution.onTrack}
          atRisk={healthDistribution.atRisk}
          offTrack={healthDistribution.offTrack}
          noCheckIn={healthDistribution.noCheckIn}
          total={healthDistribution.total}
        />
      </Card>

      {/* Compliance */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-stone-900 mb-4">Check-in Compliance</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-4 bg-success-50 rounded-lg">
            <Clock className="h-6 w-6 text-success-600 mx-auto mb-2" />
            <p className="text-2xl font-semibold text-success-700">{compliance.onTime}</p>
            <p className="text-sm text-success-600">On Time</p>
          </div>
          <div className="text-center p-4 bg-danger-50 rounded-lg">
            <AlertCircle className="h-6 w-6 text-danger-600 mx-auto mb-2" />
            <p className="text-2xl font-semibold text-danger-700">{compliance.overdue}</p>
            <p className="text-sm text-danger-600">Overdue</p>
          </div>
        </div>
        {compliance.total > 0 && (
          <p className="text-center text-sm text-stone-500 mt-4">
            {Math.round((compliance.onTime / compliance.total) * 100)}% compliance rate
          </p>
        )}
      </Card>

      {/* Confidence Trend */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-stone-900 mb-4">Confidence Trend (Last 30 Days)</h3>
        {confidenceTrend.length > 0 ? (
          <>
            <div className="flex items-end gap-2 h-32">
              {confidenceTrend.map((point, index) => {
                // Calculate bar height in pixels (max 112px to leave room for label)
                const barHeight = Math.round((point.averageConfidence / 5) * 112);
                return (
                  <div
                    key={point.date}
                    className="flex-1 flex flex-col items-center justify-end h-full"
                    title={`Week of ${point.date}: ${point.averageConfidence}/5 (${point.checkInCount} check-ins)`}
                  >
                    <div
                      className="w-full bg-primary-500 rounded-t transition-all hover:bg-primary-600"
                      style={{ height: `${barHeight}px`, minHeight: '4px' }}
                    />
                    <span className="text-xs text-stone-400 mt-1">{index + 1}</span>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-stone-400 text-center mt-2">Week</p>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-32 text-stone-400">
            <TrendingUp className="h-8 w-8 mb-2 opacity-50" />
            <p className="text-sm">No check-ins with confidence ratings yet</p>
            <p className="text-xs mt-1">Trend data will appear once team members submit check-ins</p>
          </div>
        )}
      </Card>

      {/* Team/Organization Overview */}
      <div>
        <h3 className="text-lg font-semibold text-stone-900 mb-4">{overviewTitle}</h3>
        <div className="space-y-3">
          {teamOverview.map((member) => (
            <TeamMemberCard key={member.userId} member={member} />
          ))}
        </div>
      </div>
    </div>
  );
};
